import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { openBookingModal } from "../lib/bookingModal";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/rooms", label: "Rooms" },
  { to: "/corporate", label: "Corporate" },
  { to: "/dining", label: "Restaurant" },
  { to: "/visit-holbrook", label: "Visit Holbrook" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const onHome = loc.pathname === "/";

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 60);
    f();
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);

  const dark = onHome && !scrolled;
  const txt = dark ? "text-white" : "text-[#0B192C]";

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass soft-shadow py-3" : "py-5 bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
          <Link to="/" data-testid="logo" className={`leading-none min-w-0 ${txt}`}>
            <span className="font-serif text-2xl md:text-[26px] tracking-tight inline-flex items-baseline gap-2 whitespace-nowrap">
              Byer Fountain
              {scrolled && (
                <span className="hidden xl:inline text-[#D4AF37] font-serif text-lg xl:text-xl tracking-tight font-normal">
                  Motor Inn · Holbrook
                </span>
              )}
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
                className={`text-sm tracking-wide whitespace-nowrap transition-colors hover:text-[#D4AF37] ${
                  loc.pathname === l.to ? "text-[#D4AF37]" : txt
                }`}>
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a href="tel:0260362077" data-testid="nav-call"
              className={`hidden xl:flex items-center gap-2 text-sm ${txt} hover:text-[#D4AF37] transition-colors`}>
              <Phone size={15} /> 02 6036 2077
            </a>
            <button onClick={() => openBookingModal()} data-testid="nav-book-now"
              className="hidden sm:inline-flex bg-[#D4AF37] hover:bg-[#0B192C] text-[#0B192C] hover:text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-300">
              Book Now
            </button>
            <button className={`lg:hidden ${txt}`} onClick={() => setOpen(true)} data-testid="menu-open" aria-label="Menu">
              <Menu />
            </button>
          </div>
        </nav>
      </header>

      {open && (
        <div className="fixed inset-0 z-[80] bg-[#0B192C] text-white flex flex-col p-8" data-testid="mobile-menu">
          <div className="flex justify-between items-center">
            <span className="font-serif text-2xl">Byer Fountain</span>
            <button onClick={() => setOpen(false)} data-testid="menu-close"><X /></button>
          </div>
          <div className="flex flex-col gap-6 mt-16">
            {LINKS.map((l, i) => (
              <button key={l.to} onClick={() => { setOpen(false); nav(l.to); }}
                className="text-left font-serif text-4xl hover:text-[#D4AF37] transition-colors"
                style={{ opacity: 0, animation: `fadeUp .5s ${i * 0.06}s forwards` }}>
                {l.label}
              </button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); openBookingModal(); }}
            className="mt-auto bg-[#D4AF37] text-[#0B192C] py-4 rounded-full font-medium">Book Now</button>
          <style>{`@keyframes fadeUp{to{opacity:1;transform:translateY(0)}from{transform:translateY(20px)}}`}</style>
        </div>
      )}
    </>
  );
}
